import React from 'react';
import { createRoot } from 'react-dom/client';
import TaskManager from './TaskManager/TaskManager.js';
import TaskManagerWidgetApp from './TaskManagerWidgetApp/TaskManagerWidgetApp.js';
import QMSProcedures from './QMSProcedures/QMSProcedures.js';
import ChangeAssessments from './ChangeAssessments/ChangeAssessments.js';
import TechnicalDocumentation from './TechnicalDocumentation/TechnicalDocumentation.js';
import RegulatoryDocuments from './RegulatoryDocuments/RegulatoryDocuments.js';
import FileManager from './FileManager/FileManager.js'
import MedboardPdf from './MedboardPdf/MedboardPdf.jsx';
import  { EventEmitter } from "events";
import ComplianceStandards from './ComplianceStandards/ComplianceStandards.js';
import Submissions from './Submissions/Submissions.js';
import SubmissionQAList from './Submissions/SubmissionQAList.js';
import SubmissionQAKpiCard from './Submissions/SubmissionQAKpiCard.js';
import CountryRegistrationsDocuments from './CountryRegistrationsDocuments/CountryRegistrationsDocuments.js';
import Profiles from './Profiles/Profiles.js';
import AIChat from './AIChat/AIChat.js';
import SrProjects from './SrProjects/SrProjects.js';
import SystematicReviews from './SystematicReviews/SystematicReviews.js';
import Dashboard from './Dashboard/Dashboard.js';
import UDIs from './UDIs/UDIs.js';
import RegulatoryTimelinesChart from './Shared/RegulatoryTimelinesChart.js';
import Products from './Products/Products.js';
import ProductFamilies from './ProductFamilies/ProductFamilies.js';
import Reliance from './Reliance/Reliance.js';
import CountryRegistrations from './CountryRegistrations/CountryRegistrations.js';
import CountryRegistrationsTotals from './CountryRegistrations/CountryRegistrationsTotals.js';
import CountryRegistrationsDashboard from './CountryRegistrations/CountryRegistrationsDashboard.js';
import Operators from './Operators/Operators.js';
import Certificates from './Certificates/Certificates.js';
import Watchlists from './Watchlists/Watchlists.js';

import LegalManufacturersList from './Operators/LegalManufacturersList.js';
import PhysicalManufacturersList from './Operators/PhysicalManufacturersList.js';
import SPPPsList from './Operators/SPPPsList.js';
import RegulatoryReviewsDocumentComparison from './SystematicReviews/RegulatoryReviewsDocumentComparison.js';
import GlobalDbPdfAiPane from './GlobalDatabases/GlobalDbPdfAiPane.js';


const mainReactEmitter = new EventEmitter();
window.mainReactEmitter = mainReactEmitter;
window.isTaskManagerReady = false;

const isDataTrue = (value) => value === true || value === "true" || value === "True";

const parseExternalLinkedObjects = (raw) => {
    if (!raw)
        return window.externalLinkedObjects || null;
    const text = String(raw).trim();
    if (!text)
        return window.externalLinkedObjects || null;
    if (text.startsWith("[")) {
        try {
            return JSON.parse(text);
        } catch {
            return window.externalLinkedObjects || null;
        }
    }
    const split = text.split(",");
    const linked = [];
    for (let i = 0; i + 1 < split.length; i += 2) {
        linked.push({ EntityType: split[i], EntityId: split[i + 1] });
    }
    return linked.length > 0 ? linked : (window.externalLinkedObjects || null);
};

window.startReactProjects = function (onlyLoadComponent = null) {

    const mountNodes = Array.from(document.querySelectorAll("[data-react-component]"));

    if (mountNodes && mountNodes !== null && mountNodes.length > 0) {
        mountNodes.forEach((mountNode /*, index */) => {
            if (mountNode != null) {

                const component = mountNode.dataset?.reactComponent;
                var node = null;

                // Skip other components when `onlyLoadComponent` is set — checked before
                // createRoot() now, not after: calling createRoot() again on a container
                // that already has an active root (e.g. a second, targeted
                // startReactProjects("submissions") call while this same page also has an
                // already-mounted "submission-qa-kpi" node) orphans that container's
                // existing render instead of leaving it alone.
                if (onlyLoadComponent && onlyLoadComponent != component)
                    return;

                // React 18 throws if createRoot is called twice on the same node.
                // Appraisal cards remount while the SR page Task Manager may already be live.
                if (mountNode.dataset.reactMounted === "true")
                    return;

                let root = createRoot(mountNode);
                mountNode.dataset.reactMounted = "true";


                if (component == "task-manager") {
                    const backgroundImage = mountNode.dataset.backgroundImage;
                    const paramEntityType = mountNode.dataset.entityType || window.currentEntityType;
                    const paramEntityId = mountNode.dataset.entityId || window.currentEntityId;
                    const paramIsSlimVersion = isDataTrue(mountNode.dataset.isSlimVersion) || isDataTrue(window.isSlimVersion);
                    const paramShowAllTasks = isDataTrue(mountNode.dataset.showAllTasks) || isDataTrue(window.showAllTasks);
                    const paramHasLimitedWidth = isDataTrue(mountNode.dataset.hasLimitedWidth);
                    const paramIsReviewApproved = isDataTrue(mountNode.dataset.isReviewApproved) || isDataTrue(window.isReviewApproved);
                    // Prefer per-mount data attribute. Only fall back to the window flag for
                    // page-level mounts (no entity-type), so appraisal/item embeds don't inherit
                    // leftovers from other Task Manager hosts on the same page.
                    const paramShowHeaderlessCountSummaries = mountNode.dataset.showHeaderlessCountSummaries !== undefined
                        ? isDataTrue(mountNode.dataset.showHeaderlessCountSummaries)
                        : (!mountNode.dataset.entityType && isDataTrue(window.showHeaderlessCountSummaries));
                    const paramExternalLinkedObjects = parseExternalLinkedObjects(mountNode.dataset.externalLinkedObjects);

                    node = <TaskManager
                        backgroundImage={backgroundImage}
                        emitter={mainReactEmitter}
                        paramEntityType={paramEntityType}
                        paramEntityId={paramEntityId}
                        paramIsSlimVersion={paramIsSlimVersion}
                        paramShowAllTasks={paramShowAllTasks}
                        paramHasLimitedWidth={paramHasLimitedWidth}
                        paramIsReviewApproved={paramIsReviewApproved}
                        paramShowHeaderlessCountSummaries={paramShowHeaderlessCountSummaries}
                        paramExternalLinkedObjects={paramExternalLinkedObjects}
                    />
                }
                else if (component == "task-manager-widget") {
                    const backgroundImage = mountNode.dataset.backgroundImage;
                    node = <TaskManagerWidgetApp backgroundImage={backgroundImage} />
                }
                else if (component == "qms-procedures") {
                    const backgroundImage = mountNode.dataset.backgroundImage;
                    node = <QMSProcedures />
                }
                else if (component == "change-assessments") {
                    const backgroundImage = mountNode.dataset.backgroundImage;
                    node = <ChangeAssessments />
                }
                else if (component == "technical-documentation") {
                    const backgroundImage = mountNode.dataset.backgroundImage;
                    node = <TechnicalDocumentation />
                }
                else if (component == "regulatory-documents") {
                    const backgroundImage = mountNode.dataset.backgroundImage;
                    node = <RegulatoryDocuments />
                }
                else if (component == "compliance-standards") {
                    const backgroundImage = mountNode.dataset.backgroundImage;
                    node = <ComplianceStandards />
                }
                else if (component == "file-manager") {
                    const backgroundImage = mountNode.dataset.backgroundImage;
                    node = <FileManager />
                }
                else if (component == "submissions") {
                    node = <Submissions />
                }
                else if (component == "submissions-qa") {
                    node = <SubmissionQAList />
                }
                else if (component == "submission-qa-kpi") {
                    node = <SubmissionQAKpiCard />
                }
                else if (component == "countryregistrationsdocuments") {
                    node = <CountryRegistrationsDocuments />
                }
                else if (component == "profiles") {

                    let paramProfileType = mountNode.dataset?.paramProfileType;
                    let paramProfileSlug = mountNode.dataset?.paramProfileSlug;
                    let paramProfileTab = mountNode.dataset?.paramProfileTab;

                    node = <Profiles paramProfileType={paramProfileType} paramProfileSlug={paramProfileSlug} paramProfileTab={paramProfileTab} />
                }
                else if (component == "aichat" || component == "aichat-sr-summary") {

                    let ident = mountNode.dataset?.entityid;
                    let entityType = mountNode.dataset?.entitytype;
                    let mode = mountNode.dataset?.mode;
                    let isClinicalLiterature = mountNode.dataset?.isliteraturereview;
                    let uniqueId = mountNode.dataset?.uniqueid;
                    // Only treat as having a PDF when MediaPath/PublicUrl is present, or an uploaded file id exists with no empty mediapath placeholder
                    const mediaPath = mountNode.dataset?.mediapath;
                    const userFilePath = mountNode.dataset?.userfilepath;
                    const filePath = (mediaPath && mediaPath !== "null" && mediaPath !== "")
                        ? mediaPath
                        : (userFilePath && userFilePath !== "null" && userFilePath !== ""
                            ? `/Umbraco/Api/SurveilanceAppraisal/LoadPdf?surveillanceItemId=${userFilePath}`
                            : "");

                    let documentTitle = mountNode.dataset?.documenttitle;
                    let systematicReviewType = mountNode.dataset?.systematicreviewtype;
                    let otherDocVersions = mountNode.dataset?.otherdocversions === "true";
                    let enableAIOverviewButton = false;
                    let enableUserChat = true;
                    let defaultAiTask = "question";
                    let enableComparisonButton = false;
                    let enableRequirementsButton = false;

                    let translationMediaPath = mountNode.dataset?.translationmediapath;

                    if (mode == "assessment-card") {
                        enableAIOverviewButton = true;
                        enableUserChat = true;

                        if (!isClinicalLiterature) {
                            enableComparisonButton = true;
                            enableRequirementsButton = true;
                        }
                    }

                    if (mode == "ai-summary") {
                        enableUserChat = true;
                        defaultAiTask = "sr-summary";
                    }

                    if (mode == "ai-summary-newsletter") {
                        enableUserChat = true;
                        defaultAiTask = "sr-summary-newsletter";
                    }


                    node = <AIChat entityId={ident} entityType={entityType} uniqueId={uniqueId} enableComparisonButton={enableComparisonButton} enableRequirementsButton={enableRequirementsButton} enableAIOverviewButton={enableAIOverviewButton} enableUserChat={enableUserChat} isClinicalLiterature={isClinicalLiterature} defaultAiTask={defaultAiTask} systematicReviewType={systematicReviewType} filePath={filePath} otherDocVersions={otherDocVersions} documentTitle={documentTitle} translationMediaPath={translationMediaPath} />
                }
                else if (component == "systematicreviews") {
                    node = <SystematicReviews />
                }
                else if (component == "sr-projects") {
                    node = <SrProjects />
                }
                else if (component == "dashboard") {
                    let userId = mountNode.dataset?.userid;
                    node = <Dashboard userId={userId} />
                }
                else if (component == "basic-udis") {
                    node = <UDIs />
                }
                else if (component == "regulatory-timelines-highcharts-gantt") {
                    node = <RegulatoryTimelinesChart />
                }
                else if (component == "rims-products") {
                    node = <Products />
                }
                else if (component == "rims-product-families") {
                    node = <ProductFamilies />
                }
                else if (component == "reliance") {
                    node = <Reliance />
                }
                else if (component == "rims-registrations") {
                    node = <CountryRegistrations />
                }
                else if (component == "rims-registrations-totals") {
                    node = <CountryRegistrationsTotals />
                }
                else if (component == "rims-registrations-dashboard") {
                    let legalManufacturerId = mountNode.dataset?.legalmanufacturerid;

                    node = <CountryRegistrationsDashboard legalManufacturerId={legalManufacturerId} />
                }
                else if (component == "operators") {
                    node = <Operators />
                }
                else if (component == "certificates") {
                    node = <Certificates />
                }
                else if (component == "watchlists") {
                    node = <Watchlists />
                }
                else if (component == "operators-legal-manufacturers") {
                    node = <LegalManufacturersList />
                }
                else if (component == "operators-physical-manufacturers") {
                    node = <PhysicalManufacturersList />
                }
                else if (component == "operators-sppp") {
                    node = <SPPPsList />
                }
                else if (component == "regulatory-reviews-document-comparison") {

                    let entityType = mountNode.dataset?.entitytype;

                    let originalUniqueId = mountNode.dataset?.originaluniqueid;
                    let originalMediaPath = mountNode.dataset?.originalmediapath;
                    let originalTitle = mountNode.dataset?.originaltitle;
                    let originalTranslationMediaPath = mountNode.dataset?.originaltranslationmediapath;
                    let comparisonUniqueId = mountNode.dataset?.comparisonuniqueid;
                    let comparisonMediaPath = mountNode.dataset?.comparisonmediapath;
                    let comparisonTitle = mountNode.dataset?.comparisontitle;
                    let comparisonTranslationMediaPath = mountNode.dataset?.comparisontranslationmediapath;

                    node = <RegulatoryReviewsDocumentComparison entityType={entityType} originalUniqueId={originalUniqueId} originalMediaPath={originalMediaPath} originalTranslationMediaPath={originalTranslationMediaPath} originalTitle={originalTitle} comparisonUniqueId={comparisonUniqueId} comparisonMediaPath={comparisonMediaPath} comparisonTranslationMediaPath={comparisonTranslationMediaPath}  comparisonTitle={comparisonTitle} />
                }
                else if (component == "global-db-pdf-ai") {
                    let uniqueId = mountNode.dataset?.uniqueid;
                    let mediaPath = mountNode.dataset?.mediapath;
                    let translationMediaPath = mountNode.dataset?.translationmediapath;
                    let title = mountNode.dataset?.title;
                    let surveillanceType = mountNode.dataset?.surveillancetype;

                    node = <GlobalDbPdfAiPane
                        uniqueId={uniqueId}
                        mediaPath={mediaPath}
                        translationMediaPath={translationMediaPath}
                        title={title}
                        surveillanceType={surveillanceType}
                    />
                }


                //root.render(<React.StrictMode>{node}</React.StrictMode>);
                root.render(node);
            }
        });
    }
}


window.startReactProjects();



//console.log("Setting global");
window.medboardPdf = function(mountNode, url, surveilanceItem, surveilanceId, savedComments, customKeywordHighlights){ 
    const firstEmitter = new EventEmitter(); 
    const root = createRoot(mountNode);
    var node = <MedboardPdf url={url} updater={firstEmitter} surveilanceItem={surveilanceItem} surveilanceId={surveilanceId} savedComments={savedComments} customKeywordHighlights={customKeywordHighlights}/>
    root.render(<React.StrictMode>{node}</React.StrictMode>); 
    return {
        setHighlights(highlights, appraisalType) {
            firstEmitter.emit("sethighlights", {highlights, appraisalType}); 
        },
        scrollToHighlight(id){
            firstEmitter.emit("scrollto", id); 
        },
        showCustomKeywordHighlighting(bool){
            firstEmitter.emit("showCustomKeywordHighlighting", bool); 
        }

    }
}